/**
 * VR Help: the basics (controllers, hands, the menu, people, comfort) as a 3D
 * panel opened from the VR user menu, plus "Watch the video": a tutorial on a
 * 16:9 screen inside the panel. Replaces the menu while open, like the emote
 * and world-hop panels. Copy lives in vrHelpContent.ts.
 *
 * Quest budget: unlit materials only; the video element exists only while the
 * video view is open (released on leaving it), one texture upload per new frame.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { vrColors as colors, noRaycast, Surface } from './vrUi';
import { HELP_TOPICS, HELP_VIDEO, formatTime, layoutItems } from './vrHelpContent';

const W = .76, H = .54, TOP = H / 2;
const TAB_W = .19, TAB_X = -W / 2 + .02 + TAB_W / 2;
// Content column: right of the tabs to the panel's right margin.
const C_LEFT = -W / 2 + .02 + TAB_W + .02, C_RIGHT = W / 2 - .02, C_W = C_RIGHT - C_LEFT, C_X = (C_LEFT + C_RIGHT) / 2;
const FONT = .0175, KEY_W = .13, VAL_W = C_W - KEY_W - .045;
const SCREEN_W = C_W - .03, SCREEN_H = SCREEN_W * 9 / 16, BAR_W = SCREEN_W - .25;

function Button({ label, x, y, w, h = .05, active = false, accent = false, disabled = false, fontSize = .016, name, onPress }: {
  label: string; x: number; y: number; w: number; h?: number; active?: boolean; accent?: boolean; disabled?: boolean; fontSize?: number; name: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : active ? colors.teal : hover ? colors.hover : accent ? '#124940' : colors.card;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.01} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={fontSize} maxWidth={w - .014} textAlign="center"
      color={disabled ? colors.muted : active ? '#04211e' : colors.text}>{label}</Text>
  </group>;
}

function Topic({ index }: { index: number }) {
  const topic = HELP_TOPICS[index];
  const { ys } = useMemo(() => layoutItems(topic.items, { fontSize: FONT, keyWidth: KEY_W, valueWidth: VAL_W, lineHeight: 1.25, gap: .016 }), [topic]);
  return <group position={[C_LEFT + .016, TOP - .1, .006]}>
    {topic.items.map((it, i) => <group key={it.k} position={[0, ys[i], 0]}>
      <Text raycast={noRaycast} anchorX="left" anchorY="top" fontSize={FONT} lineHeight={1.25} maxWidth={KEY_W} color={colors.teal}>{it.k}</Text>
      <Text raycast={noRaycast} position={[KEY_W + .015, 0, 0]} anchorX="left" anchorY="top" fontSize={FONT} lineHeight={1.25}
        maxWidth={VAL_W} color={colors.text}>{it.v}</Text>
    </group>)}
  </group>;
}

// Release a <video> fully: pausing alone keeps the decoder and the download.
function releaseVideo(v: HTMLVideoElement) { v.pause(); v.removeAttribute('src'); v.load(); }

/** The tutorial on a 16:9 screen. Mounted only while the video view is open. */
export function VRHelpVideo({ src = HELP_VIDEO.src, poster = HELP_VIDEO.poster, paused = false, x = 0, y = 0 }: {
  src?: string; poster?: string; paused?: boolean; x?: number; y?: number;
}) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [playing, setPlaying] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [time, setTime] = useState('0:00 / 0:00');
  const [posterTex, setPosterTex] = useState<THREE.Texture | null>(null);
  const [shown, setShown] = useState(false); // a decoded frame is on screen
  const fill = useRef<THREE.Mesh>(null);
  const lastTime = useRef(-1);

  const video = useMemo(() => {
    const v = document.createElement('video');
    v.crossOrigin = 'anonymous'; v.playsInline = true; v.preload = 'metadata';
    v.setAttribute('playsinline', '');
    return v;
  }, []);
  const tex = useMemo(() => {
    const t = new THREE.VideoTexture(video);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, [video]);

  useEffect(() => {
    const ok = () => setStatus('ready');
    const fail = () => { setStatus('error'); setPlaying(false); };
    const tick = () => setTime(`${formatTime(video.currentTime)} / ${formatTime(video.duration)}`);
    const ended = () => setPlaying(false);
    video.addEventListener('loadedmetadata', ok); video.addEventListener('error', fail);
    video.addEventListener('timeupdate', tick); video.addEventListener('durationchange', tick); video.addEventListener('ended', ended);
    video.src = src;
    return () => {
      video.removeEventListener('loadedmetadata', ok); video.removeEventListener('error', fail);
      video.removeEventListener('timeupdate', tick); video.removeEventListener('durationchange', tick); video.removeEventListener('ended', ended);
      releaseVideo(video); tex.dispose();
    };
  }, [video, tex, src]);

  // Poster is optional: a missing file just leaves the dark screen.
  useEffect(() => {
    let t: THREE.Texture | null = null, dead = false;
    new THREE.TextureLoader().load(poster, (loaded) => {
      if (dead) { loaded.dispose(); return; }
      loaded.colorSpace = THREE.SRGBColorSpace; t = loaded; setPosterTex(loaded);
    }, undefined, () => { /* no poster */ });
    return () => { dead = true; t?.dispose(); };
  }, [poster]);

  // Menu hidden (A / X, wrist): stop the sound with it.
  useEffect(() => { if (paused && !video.paused) { video.pause(); setPlaying(false); } }, [paused, video]);

  useFrame(() => {
    // VideoTexture uploads on requestVideoFrameCallback, which may not fire in
    // an immersive session (the page is never composited): flag new frames here.
    if (video.readyState >= 2 && video.currentTime !== lastTime.current) {
      lastTime.current = video.currentTime; tex.needsUpdate = true;
      // Only once it plays: with preload=metadata Chrome reports frame data
      // before any frame is uploaded, which swapped the poster for black.
      if (!shown && !video.paused) setShown(true);
    }
    const d = video.duration;
    if (fill.current) {
      const p = d > 0 && Number.isFinite(d) ? Math.min(1, video.currentTime / d) : 0;
      fill.current.scale.x = Math.max(p, .0001);
      fill.current.position.x = -BAR_W / 2 + BAR_W * p / 2;
    }
  });

  const play = () => {
    if (status === 'error') return;
    if (!video.paused) { video.pause(); setPlaying(false); return; }
    if (video.ended) video.currentTime = 0;
    setNote(null);
    // The click is a user gesture, so sound should be allowed; if the browser
    // still refuses, play muted rather than not at all.
    video.play().then(() => setPlaying(true)).catch((err: DOMException) => {
      if (err?.name !== 'NotAllowedError') { if (video.error) setStatus('error'); return; }
      video.muted = true;
      video.play().then(() => { setPlaying(true); setNote('Sound was blocked, playing muted'); }).catch(() => setNote('Could not start the video'));
    });
  };
  const restart = () => {
    if (status === 'error') return;
    video.currentTime = 0;
    if (video.paused) play();
  };

  const map = shown ? tex : posterTex;
  const ctrlY = -SCREEN_H / 2 - .045;
  return <group position={[x, y, 0]}>
    <Surface width={SCREEN_W + .01} height={SCREEN_H + .01} color={colors.border} r={.008} z={.004} />
    <mesh raycast={noRaycast} position={[0, 0, .006]}>
      <planeGeometry args={[SCREEN_W, SCREEN_H]} />
      <meshBasicMaterial key={map ? map.uuid : 'none'} map={map} color={map ? '#ffffff' : '#0a1420'} toneMapped={false} />
    </mesh>
    {status === 'error'
      ? <group position={[0, 0, .009]}>
        <Text raycast={noRaycast} position={[0, .015, 0]} fontSize={.024} color={colors.text}>Video unavailable</Text>
        <Text raycast={noRaycast} position={[0, -.022, 0]} fontSize={.0145} color={colors.muted} maxWidth={SCREEN_W - .06} textAlign="center">
          The tabs on the left cover the same basics.</Text>
      </group>
      // The poster carries its own title; the hint is only for a bare screen.
      : !playing && !shown && !posterTex && <Text raycast={noRaycast} position={[0, 0, .009]} fontSize={.022} color={colors.text}>
        {status === 'loading' ? 'Loading…' : 'Press Play'}</Text>}
    <Button name="vr-help-play" label={playing ? 'Pause' : 'Play'} x={-SCREEN_W / 2 + .045} y={ctrlY} w={.09} h={.044} accent={!playing} disabled={status === 'error'} onPress={play} />
    <Button name="vr-help-restart" label="Restart" x={-SCREEN_W / 2 + .145} y={ctrlY} w={.09} h={.044} disabled={status === 'error'} onPress={restart} />
    <group position={[-SCREEN_W / 2 + .2 + BAR_W / 2, ctrlY + .008, .006]}>
      <Surface width={BAR_W} height={.012} color={colors.card} r={.006} />
      <mesh ref={fill} raycast={noRaycast} position={[-BAR_W / 2, 0, .002]} scale={[.0001, 1, 1]}>
        <planeGeometry args={[BAR_W, .008]} />
        <meshBasicMaterial color={colors.teal} toneMapped={false} />
      </mesh>
    </group>
    <Text raycast={noRaycast} position={[SCREEN_W / 2, ctrlY - .016, .006]} anchorX="right" fontSize={.0125} color={colors.muted}>{time}</Text>
    {note && <Text raycast={noRaycast} position={[-SCREEN_W / 2 + .2, ctrlY - .016, .006]} anchorX="left" fontSize={.0125} color={colors.teal}>{note}</Text>}
  </group>;
}

export default function VRHelpPanel({ onClose, paused = false, videoSrc, videoPoster }: {
  onClose: () => void;
  /** True while the menu is hidden: pauses the video. */
  paused?: boolean;
  /** Overrides for testing; default HELP_VIDEO. */
  videoSrc?: string; videoPoster?: string;
}) {
  const [tab, setTab] = useState<number | 'video'>(0);
  const title = tab === 'video' ? 'Watch the video' : HELP_TOPICS[tab].title;
  return <group onClick={(e) => e.stopPropagation()} name="vr-help-panel">
    <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} />
    <Surface width={W} height={H} color={colors.panel} />
    <Text raycast={noRaycast} position={[-W / 2 + .02, TOP - .035, .006]} anchorX="left" fontSize={.021} color={colors.text}>Help</Text>
    <Text raycast={noRaycast} position={[C_LEFT + .016, TOP - .035, .006]} anchorX="left" fontSize={.018} color={colors.teal} maxWidth={C_W - .14}>{title}</Text>
    <Button name="vr-help-close" label="Back" x={W / 2 - .07} y={TOP - .035} w={.1} h={.042} onPress={onClose} />

    {HELP_TOPICS.map((t, i) => <Button key={t.id} name={`vr-help-tab-${t.id}`} label={t.tab} x={TAB_X} y={TOP - .1 - i * .06}
      w={TAB_W} h={.05} fontSize={.0155} active={tab === i} onPress={() => setTab(i)} />)}
    <Button name="vr-help-tab-video" label="Watch the video" x={TAB_X} y={TOP - .1 - HELP_TOPICS.length * .06 - .025}
      w={TAB_W} h={.058} fontSize={.0155} active={tab === 'video'} accent onPress={() => setTab('video')} />
    <Text raycast={noRaycast} position={[TAB_X, -TOP + .03, .006]} fontSize={.0115} color={colors.muted} maxWidth={TAB_W} textAlign="center">
      Help is always in your menu</Text>

    <group position={[C_X, -.03, 0]}><Surface width={C_W} height={H - .09} color="#162434" z={.002} /></group>
    {tab === 'video'
      ? <VRHelpVideo src={videoSrc} poster={videoPoster} paused={paused} x={C_X} y={TOP - .08 - SCREEN_H / 2 - .015} />
      : <Topic index={tab} />}
  </group>;
}
